"use strict";

import jQuery from 'jquery';

/**
 * @namespace
 * @memberof gc.gis
 */
gc.gis.util = gc.gis.util || {};

/**
 * @typedef gc.gis.GCFormatUnitObject
 * @name gc.gis.GCFormatUnitObject
 * @property {Number} value (tatsächlicher) numerischer Wert
 * @property {Number} formattedValue Formatierter Wert (ggf. in Einheit konvertiert, begrenzte Kommastellen)
 * @property {string} unit Maßeinheit des formatierten Werts
 * @property {string} unitHtml Maßeinheit ggf. in HTML-Auszeichnung des formatierten Werts
 */

/**
 *	Statische Klasse mit Hilfsfunktionen
 *
 *	@class
 *	@static
 *	@author	Anton Popov <a.popov@brain-scc.de>
 *	@author	Christian Schmidt <c.schmidt@brain-scc.de>
 */
gc.gis.GCUtil = {

	/**
	 * Die Methode assign kopiert die Werte aller aufzählbaren, eigenen Eigenschaften von einem oder mehreren
	 * Quellobjekten in ein Zielobjekt. Es wird das Zielobjekt zurückgegeben.
	 *
	 * Polyfill für Object.assign(). Nicht im IE enthalten.
	 *
	 *
	 * @param {Object} target Das Zielobjekt.
	 * @param {...Object} var_sources Das/Die Quellobjekt(e).
	 * @return {Object} Das erweiterte Zielobjekt.
	 * @see https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
	 */
	assign: (typeof Object.assign === 'function') ? Object.assign : function(target, var_sources)
	{
		if (target === undefined || target === null)
		{
			throw new TypeError('Cannot convert undefined or null to object');
		}

		var to = Object(target);
		for (var i = 1, ii = arguments.length; i < ii; i++)
		{
			var nextSource = arguments[i];
			if (nextSource !== undefined && nextSource !== null)    // Skip over if undefined or null
			{
				for (var key in nextSource)
				{
					if (nextSource.hasOwnProperty(key))
					{
						to[key] = nextSource[key];
					}
				}
			}
		}

		return to;
	},

	/**
	 *	Prüft, ob der übergebene Parameter entweder exakt true oder false ist.
	 *	Falls weder noch, wird der übergebene Defaultwert zurückgegeben.
	 *
	 *	@param {*}	value	zu prüfender Wert
	 *	@param {boolean}	default_value	im Fehlerfall zurückzugebender Wert
	 */
	parseBool: function( value, default_value )
	{
		gc.gis.assert.isBool( default_value );

		return (value === true || value === 'true') ?
			true  : ((value === false || value === 'false') ?
			false : default_value);
	},

	/**
	 *	Prüft, ob eine Zeichenkette mit einer anderen Zeichenkette aufhört.
	 *
	 *	@param {string}	str	zu prüfender String
	 *	@param {string}	query	potentielles Zeichenkettenende
	 *	@return {boolean}	true, wenn
	 */
	endsWith: function( str, query )
	{
		var match = false;

		if( str && query )
		{
			var i_start = str.length - query.length;
			for( var i=i_start; i<str.length; i++ )
			{
				if( str[i] == query[i-i_start] )
				{
					match = true;
				}
				else
				{
					match = false;
					break;
				}
			}
		}
		return match;
	},

	/**
	 *	Berechnet die gemeinsame Ausdehnung der übergebenen Extents.
	 *
	 *	@param {gc.gis.map.GCMapExtent[]}	extents	Liste von Extents
	 *	@return {gc.gis.map.GCMapExtent}	Ausdehnung [xmin,ymin,xmax,ymax]
	 */
	mergeExtents: function( extents )
	{
		gc.gis.assert.isArray( extents );
		var extent = extents[0];

		if( extents.length > 1 )
		{
			for( var i=1; i<extents.length; i++ )
			{
				extent = this.expandExtent( extent, extents[i] );
			}
		}
		return extent;
	},

	/**
	 *	Berechnet die gemeinsame Ausdehnung der übergebenen Extents.
	 *
	 *	@param {gc.gis.map.GCMapExtent}	first_extent	erster Extent
	 *	@param {gc.gis.map.GCMapExtent}	second_extent	zweiter Extent
	 *	@return {gc.gis.map.GCMapExtent}	Ausdehnung [xmin,ymin,xmax,ymax]
	 */
	expandExtent: function( first_extent, second_extent )
	{
		gc.gis.assert.isArray( first_extent );
		gc.gis.assert.isArray( second_extent );

		var extent = this.copyArray( first_extent );

		// xMin
		if( second_extent[0] < extent[0] )
		{
			extent[0] = second_extent[0];
		}
		// yMin
		if( second_extent[1] < extent[1] )
		{
			extent[1] = second_extent[1];
		}
		// xMax
		if( second_extent[2] > extent[2] )
		{
			extent[2] = second_extent[2];
		}
		// yMax
		if( second_extent[3] > extent[3] )
		{
			extent[3] = second_extent[3];
		}

		return extent;
	},

	/**
	 *	Tauscht XY-Koordinaten in YX-Koordinaten und umgekehrt.
	 *
	 *	@param {number[]|number[][]}	input	Eingabekoordinaten (kann verschachtelt sein)
	 *	@param {number}	opt_dim	Optionale Koordinatendimension, default 2
	 *	@return {number[]|number[][]}	Ausgabekoordinaten
	 */
	flipAxis: function( input, opt_dim )
	{
		var dim = opt_dim || 2;
		var output = [];
		for( var i=0; i<input.length; i+=dim )
		{
			var tmp = input[i];
			if( typeof tmp !== 'number' )
			{
				tmp = gc.gis.GCUtil.flipAxis( tmp );
				output[i] = tmp;
				i-=(dim-1);
			}
			else
			{
				output[i]   = input[i+1];
				output[i+1] = input[i];
			}
		}
		return output;
	},


	/**
	 * Klemmpt den angegebenen Zahlwert zwischen den beiden Grenzen fest.
	 *
	 * @param {number} value Zahlenwert
	 * @param {number} min minimaler Wert, der zurückgebene werden darf.
	 * @param {number} max maximaler Wert, der zurückgebene werden darf.
	 * @return {number} der angegebene Zahlenwert, soferm er zwischen den Grenzen liegt oder den nächstgelegenen
	 *                  passenden Wert innerhalb der Grenze.
	 */
	clamp: function(value, min, max)
	{
		return Math.min(Math.max(value, min), max);
	},

	/**
	 * Kopiert die Einträge des Eingabearrays in ein neues Array.
	 *
	 * @param {Array} input	Eingabearray
	 * @return {Array} Kopie des Eingabearrays
	 */
	copyArray: function( input )
	{
		const output = [];
		for (let i = 0, ii = input.length; i < ii; i++)
		{
			output.push(input[i]);
		}
		return output;
	},

	/**
	 * Entfernt doppelte Werte aus einem Array.
	 *
	 * @param {Array} input	Eingabearray
	 * @return {Array} Kopie des Eingabearrays
	 */
	uniqueArray: function(input)
	{
		const unique = function(value, index, self) {
			return self.indexOf(value) === index;
		}
		return input.filter(unique);
	},

	/**
	 *    Entfernt doppelte Werte aus einem Array.
	 *
	 * @param {Array} inputA Eingabearray A
	 * @param {Array} inputB Eingabearray B
	 * @return {Array} neues Array, welches nur Elemente enthält, die in A und B enthalten sind
	 */
	intersectArray: function(inputA, inputB)
	{
		let tmp;
		if (inputB.length > inputA.length) {
			tmp = inputB;
			inputB = inputA;
			inputA = tmp;
		}
		return inputA.filter(function (element) {
			return inputB.indexOf(element) > -1;
		});
	},

	/**
	 *	Sucht die am tiefsten verschachtelte, gemeinsame Elterngruppe einer
	 *	Liste von Layern.
	 *
	 *	@param {gc.gis.layer.GCAbstractLayer[]}	layers	Liste von zu untersuchenden Layern
	 *	@return {gc.gis.layer.GCGroupLayer}	gemeinsame Elterngruppe
	 */
	findCommonParent: function( layers )
	{
		var root = gc.gis.iface.map.getRoot();
		var indices = [];
		var common_parent;

		// Indizes zusammentragen
		var max_level;
		for( var i=0; i<layers.length; i++ )
		{
			var index = layers[i].getIndex();
			// console.log('prep.i='+i+': layer='+layers[i].getLabel()+'  index='+index);
			if( max_level === undefined )
			{
				max_level = index.length;
			}
			else
			{
				max_level = Math.min( max_level, index.length );
			}
			indices.push( index );
			//console.log('prep.i='+i+': max_level='+max_level+'  indices='+indices);
		}

		// Indizes gleicher Tiefe vergleichen, bis erster Unterschied auftaucht
		var match = false;
		var level = 0;
		var matching_index_path = [];
		while( !match )
		{
			// console.log('work.matching_path='+matching_index_path);
			var nomatch = false;
			for( var i=1; i<indices.length; i++ )
			{
				if( level < indices[i-1].length
				 && level < indices[i].length )
				{
					if( !indices[i].length || !indices[i-1].length )
					{
						nomatch = true;
					}
					else if( indices[i-1][level] !== indices[i][level] )
					{
						nomatch = true;
					}
				}
			}

			// gleiche Indizes auf gleicher Tiefe => nächste Tiefe vergleichen
			if( nomatch === false
			 && level < max_level-1
			 && typeof indices[0][level] === 'number' )
			{
				matching_index_path.push( indices[0][level] );
				level++;
				continue;
			}
			// stop
			else
			{
				if( matching_index_path.length > 0 )
				{
					common_parent = root.findLayer( matching_index_path );
				}
				else
				{
					common_parent = root;
				}
				match = true;
			}
		}

		return common_parent || root;
	},

	/**
	 *	Kopiert Text in die Zwischenablage, sofern vom Browser unterstützt.
	 *
	 *	@param {string}	content	Zu kopierende Zeichenkette
	 *	@return {void}
	 */
	copyToClipboard: function( content )
	{
		var copy_supported = false;
		if( document.queryCommandSupported
		 && document.queryCommandSupported('copy') === true )
		{
			copy_supported = true;
		}

		if( copy_supported && document.execCommand )
		{
			// temporäres Control erstellen
			var ta = jQuery('<textarea>', {
				value: content,
				text: content
			});
			jQuery('body').append( ta );
			ta.select();

			// Kopieren
			var success = false;
			try
			{
				success = document.execCommand('copy', false, null );

				// Erfolg auswerten
				if( success )
				{
					gc.gis.iface.ui.notify({
						type: gc.gis.ui.GCNotificationType.INFO,
						title: 'In Zwischenablage kopiert',
						message: '"'+ content +'"'
					});
				}
				else
				{
					throw new gc.gis.error.GCError('Failed to copy to clipboard');
				}
			}
			catch( err )
			{
				gc.gis.iface.log.error( this, err );
			}
			finally
			{
				// temporäres Control entfernen
				ta.remove();
			}
		}
		else
		{
			gc.gis.iface.log.error( this,
				new gc.gis.error.GCUnsupportedActionError(
					'Copy to clipboard'
				)
			);
		}
	},

	/**
	 * Erstellt eine sehr simple Checksumme für einen String.
	 * Achtung: Nicht für kritische Dinge verwenden!
	 * Quelle: http://stackoverflow.com/questions/811195/fast-open-source-checksum-for-small-strings#811277
	 *
	 * @param {string} str
	 * @return {string}
     */
	checksum: function(str)
	{
		var i;
		var chk = 0x12345678;

		for (i = 0; i < str.length; i++) {
			chk += (str.charCodeAt(i) * (i + 1));
		}

		return ''+chk;
	},

	/**
	 * Erzeugt einen einfachen Hash-Code für Strings.
	 *
	 * Der Algorithmus wurde in Anlehnung an die Java-Methode hashCode umgesetzt.
	 *
	 * @param {string} str
	 * @return {number}
	 */
	hashCode: function(str)
	{
		var hash = 0,
			char,
			i, ii;

		if (!str || str.length === 0)
			return hash;

		for (i = 0, ii = str.length; i < ii; i++)
		{
			char = str.charCodeAt(i);
			hash = ((hash << 5) - hash) + char;
			hash = hash & hash; // Convert to 32bit integer
		}

		return hash;
	},

	/**
	 * Rechnet ein Winkelmaß von Gon in Grad um.
	 *
	 * @method
	 * @param {number} gon Gon
	 * @return {number} Grad
	 * @public
	 */
	gonToDeg: function(gon)
	{
		return gon * 360 / 400;
	},

	/**
	 * Rechnet ein Winkelmaß von Grad in Gon um.
	 *
	 * @method
	 * @param {number} deg
	 * @return {number}
	 * @public
	 */
	degToGon: function(deg)
	{
		return deg * 400 / 360;
	},

	/**
	 * Formatiert ein Flächenmaß.
	 *
	 * @public
	 * @method
	 * @param {Number} area
	 * @return {gc.gis.GCFormatUnitObject}
	 */
	formatArea: function(area)
	{
		var formatted_value = area;

		if(area < 1 / (100 * 100))
		{
			formatted_value *= (100 * 100);
			return {
				formattedValue: formatted_value.toFixed(2),
				value: area,
				unit: 'cm²',
				unitHtml: 'cm<sup>2</sup>'
			};
		}

		if(area > 1000 * 1000)
		{
			formatted_value /= (1000*1000);
			return {
				formattedValue: formatted_value.toFixed(2),
				value: area,
				unit: 'km²',
				unitHtml: 'km<sup>2</sup>'
			};
		}

		return {
			formattedValue: formatted_value.toFixed(2),
			value: area,
			unit: 'm²',
			unitHtml: 'm<sup>2</sup>'
		};
	},

	/**
	 * Formatiert ein Längenmaß.
	 *
	 * @public
	 * @method
	 * @param {Number} distance
	 * @return {gc.gis.GCFormatUnitObject}
	 */
	formatLength: function(distance)
	{
		var formatted_value = distance;

		if(distance < 1)
		{
			formatted_value *= 100;
			return {
				formattedValue: formatted_value.toFixed(2),
				value: distance,
				unit: 'cm',
				unitHtml: 'cm'
			};
		}

		if(distance > 1000)
		{
			formatted_value /= 1000;
			return {
				formattedValue: formatted_value.toFixed(2),
				value: distance,
				unit: 'km',
				unitHtml: 'km'
			};
		}

		return {
			formattedValue: formatted_value.toFixed(2),
			value: distance,
			unit: 'm',
			unitHtml: 'm'
		};
	},

	stripTags: function (text) {
		return text.replace(/(<([^>]+)>)/ig,"");
	}
};

